<template>
  <section class="app-member-reviews-list-item">
    <header class="item-header">
      <figure class="avatar">
        <img v-if="avatar" class="avatar-img" loading="lazy" :src="avatar.url" :alt="avatar.alt" />
        <AppImageStub v-else class="avatar-img" />
      </figure>

      <div class="meta">
        <div class="meta-info">
          <span v-if="userName" class="meta-info-line username">{{ userName }}</span>
          <span v-if="review.createdAtForHuman" class="meta-info-line datetime">{{ review.createdAtForHuman }}</span>
        </div>

        <div class="likes-wrapper">
          <div class="likes-count">{{ review.likes }}</div>
          <button v-if="review.canBeLiked" class="like-element like-element--button" @click="handleLikeClick">
            <AppLoader v-if="likeProcessing" :border-width="2" :margin="0" :size="16" />
            <TheHeartIcon v-else :filled="review.likedByCurrentUser" />
          </button>
          <span v-else class="like-element">
            <TheHeartIcon disabled />
          </span>
        </div>
      </div>
    </header>

    <div class="text">{{ review.text }}</div>

    <slot />
  </section>
</template>

<script>
import toggleLikeMutation from '@/graphql/like/ToggleLike.mutation.gql';
import TheHeartIcon from '@/components/icons/TheHeartIcon.vue';
import AppImageStub from '@/components/AppImageStub.vue';

export default {
  name: 'AppReviewableListItem',
  components: { AppImageStub, TheHeartIcon },
  props: {
    review: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      likeProcessing: false,
    };
  },
  computed: {
    avatar() {
      const avatarUrl = this.review.user?.profile_picture_full_url || null;

      return avatarUrl
        ? {
            alt: this.userName || 'User avatar',
            url: avatarUrl,
          }
        : null;
    },
    userName() {
      return this.review.user?.name;
    },
  },
  methods: {
    handleLikeClick() {
      if (this.likeProcessing) {
        return;
      }

      this.likeProcessing = true;

      this.$apollo
        .mutate({
          mutation: toggleLikeMutation,
          variables: {
            id: this.review.id,
          },
        })
        .then(() => {
          this.$emit('likes-changed');
        })
        .catch((err) => {
          this.$toast.error(err?.graphQLErrors?.[0]?.message);
        })
        .finally(() => {
          this.likeProcessing = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-member-reviews-list-item {
  display: flex;
  flex-direction: column;
  gap: 24px;

  .item-header {
    display: flex;
    gap: 18px;
  }

  .avatar {
    flex-shrink: 0;

    .avatar-img {
      width: 46px;
      height: 46px;
      object-fit: cover;
      border-radius: 50%;
      background-color: vars.$c-img-bg;
    }
  }

  .meta {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .meta-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .meta-info-line {
    font-size: 16px;

    &.username {
      font-size: 18px;
      font-weight: 500;
    }

    &.datetime {
      font-weight: 400;
      color: vars.$c-gray-a2;
    }
  }

  .likes-wrapper {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .likes-count {
    color: vars.$c-dark-gray;
    font-size: 16px;
    line-height: 28px;
  }

  .like-element {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;

    &:not(&--button) {
      cursor: not-allowed;
    }

    &--button {
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.8;
      }
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .text {
    color: vars.$c-dark-gray;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    word-break: break-word;

    @media (min-width: vars.$tablet-break-point + 1) {
      font-size: 18px;
    }
  }
}
</style>
